import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { makeStyles } from '@material-ui/core/styles'
import PlanHeader from './PlanHeader'
import PlanFilterPills from './filters/PlanFilterPills'
import PlanCard from './board/PlanCard'
import PlanCategoryGroup from './board/PlanCategoryGroup'
import PlanItemDetailPanel from './detailPanel/PlanItemDetailPanel'
import boardStyles from './board/styles'
import {
  applyStatusFilter,
  categoryHasMatchingItem,
  countTotalItems,
  deriveFilterPills,
  findNode,
  resolveActiveLevels
} from './helpers'
import { DEFAULT_CATEGORY_METADATA, DEFAULT_ITEM_METADATA } from './constants'

const PANEL_TRANSITION_MS = 300
const DEFAULT_PANEL_WIDTH = 600

const useStyles = makeStyles((theme) => ({
  bottomRow: {
    display: 'flex',
    gap: 'var(--dim-4)',
    alignItems: 'flex-start',
    width: '100%'
  },
  panelWrapper: ({ panelWidth }) => ({
    position: 'sticky',
    top: 'var(--dim-4)',
    alignSelf: 'flex-start',
    flex: `0 0 ${panelWidth}px`,
    maxWidth: `${panelWidth}px`,
    transform: 'translateX(100%)',
    opacity: 0,
    transition: 'transform 0.3s ease-out, opacity 0.3s ease-out',
    '&.__visible': {
      transform: 'translateX(0)',
      opacity: 1
    }
  }),
  report: {
    width: '100%',
    gap: 'var(--dim-4)',
    display: 'flex',
    flexDirection: 'column',
    ...boardStyles(theme)
  },
  emptyFiltered: {
    minHeight: '120px',
    color: 'var(--text-color-subtle)'
  }
}))

// `null`/missing on config means "use defaults". An explicit empty array is
// respected as "show no rows". This preserves backwards compatibility with
// blueprints that pre-date SMT-1717.
function resolveMetadataConfig (configValue, defaults) {
  if (configValue === undefined || configValue === null) return defaults
  return Array.isArray(configValue) ? configValue : defaults
}

function PlanBoard ({ plan, config, displayName }) {
  const panelWidth = Number.isFinite(config?.panelWidth) && config.panelWidth > 0
    ? config.panelWidth
    : DEFAULT_PANEL_WIDTH
  const classes = useStyles({ panelWidth })
  const [currentFilter, setCurrentFilter] = useState(null)
  const [selectedItemId, setSelectedItemId] = useState(null)
  const [panelVisible, setPanelVisible] = useState(false)
  const closeTimeoutRef = useRef(null)

  const statusStyleOverrides = config?.statusStyleOverrides
  const taskComponent = config?.taskComponent
  const showPlanCard = config?.showPlanCard !== false
  const showPlanCardStatus = config?.showPlanCardStatus !== false

  const levels = useMemo(() => resolveActiveLevels(config?.aggregationLevels), [config?.aggregationLevels])
  const itemMetadata = useMemo(
    () => resolveMetadataConfig(config?.itemMetadata, DEFAULT_ITEM_METADATA),
    [config?.itemMetadata]
  )
  const categoryMetadata = useMemo(
    () => resolveMetadataConfig(config?.categoryMetadata, DEFAULT_CATEGORY_METADATA),
    [config?.categoryMetadata]
  )

  const pills = useMemo(() => deriveFilterPills(plan, statusStyleOverrides, levels), [plan, statusStyleOverrides, levels])
  const totalCount = useMemo(() => countTotalItems(plan, levels), [plan, levels])
  const filteredPlan = useMemo(() => applyStatusFilter(plan, currentFilter, levels), [plan, currentFilter, levels])
  const selected = useMemo(() => findNode(plan, selectedItemId), [plan, selectedItemId])

  const handleClose = useCallback(() => {
    setPanelVisible(false)
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current)
    closeTimeoutRef.current = setTimeout(() => {
      closeTimeoutRef.current = null
      setSelectedItemId(null)
    }, PANEL_TRANSITION_MS)
  }, [])

  useEffect(() => () => {
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current)
  }, [])

  const handleSelectItem = useCallback((itemId) => {
    if (itemId === selectedItemId) {
      handleClose()
      return
    }
    setSelectedItemId(itemId)
  }, [selectedItemId, handleClose])

  useEffect(() => {
    if (!selectedItemId) return
    requestAnimationFrame(() => setPanelVisible(true))
  }, [selectedItemId])

  useEffect(() => {
    if (selectedItemId && !findNode(plan, selectedItemId)) {
      handleClose()
    }
  }, [plan, selectedItemId, handleClose])

  useEffect(() => {
    if (!selectedItemId) return undefined
    const onKey = (e) => {
      if (e.key === 'Escape') handleClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [selectedItemId, handleClose])

  const categoryGroups = (filteredPlan?.data ?? []).map(category => (
    <PlanCategoryGroup
      key={category.id}
      category={category}
      statusStyleOverrides={statusStyleOverrides}
      selectedItemId={selectedItemId}
      onSelectItem={handleSelectItem}
      defaultExpanded={categoryHasMatchingItem(category, currentFilter, levels)}
    />
  ))

  const emptyMessage = (
    <div className={`${classes.emptyFiltered} flex items-center justify-center font-size-sm`}>
      No items match this filter
    </div>
  )

  const detailPanelProps = selected ? {
    node: selected.node,
    kind: selected.kind,
    category: selected.kind === 'item' ? selected.parent : null,
    metadataRows: selected.kind === 'item' ? itemMetadata : categoryMetadata,
    statusStyleOverrides,
    taskComponent,
    onClose: handleClose
  } : null

  return (
    <div className='flex flex-col w-full'>
      <PlanHeader
        plan={plan}
        title={config?.title || plan?.name || displayName}
        statusStyleOverrides={statusStyleOverrides}
      />
      <PlanFilterPills
        pills={pills}
        totalCount={totalCount}
        currentFilter={currentFilter}
        onFilter={setCurrentFilter}
        allItemsLabel={config?.allItemsLabel || 'All Statuses'}
      />
      <div className={classes.bottomRow}>
        <div className='flex-1' style={{ minWidth: 0 }}>
          <div className={classes.report}>
            <PlanCard
              plan={plan}
              cardLabel={config?.planCardLabel || 'The Plan'}
              showHeader={showPlanCard}
              showStatus={showPlanCard && showPlanCardStatus}
              statusStyleOverrides={statusStyleOverrides}
            >
              {categoryGroups.length ? categoryGroups : emptyMessage}
            </PlanCard>
          </div>
        </div>

        {detailPanelProps ? (
          <div className={clsx(classes.panelWrapper, { __visible: panelVisible })}>
            <PlanItemDetailPanel {...detailPanelProps} />
          </div>
        ) : null}
      </div>
    </div>
  )
}

PlanBoard.propTypes = {
  plan: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    status: PropTypes.string,
    metadata: PropTypes.object,
    data: PropTypes.array
  }).isRequired,
  config: PropTypes.object,
  displayName: PropTypes.string
}

export default PlanBoard
